import fetcher from "../fetcher";
import { pickCashQuote } from "./DeskingHelper";

const getLeadId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lead_id') || undefined;
};

// A Cash deal has a single, deterministic quote (the forced-term '36' cash
// price), so there is nothing to choose in the "Quotes for…" grid. This runs the
// cash desking scan, auto-picks that quote, creates the deal, and hands the saved
// deal to onSave — all through plain API calls, so the DeskingModal never mounts
// and the user lands straight on the cash quote page.
//
// onNeedsGrid is the fallback: called (to open the grid modal) only when the scan
// errors or returns no eligible cash quote, so the user can still resolve it.
export async function createCashDealDirect(deskingData, { onSave, onNeedsGrid } = {}) {
    let deskingState;
    try {
        const scanResp = await fetcher.getDesking(deskingData);
        deskingState = scanResp?.data;
    } catch (e) {
        onNeedsGrid && onNeedsGrid();
        return;
    }

    const pick = pickCashQuote(deskingState?.quotes, deskingData?.credit_score);
    if (!pick) {
        // Nothing eligible to auto-pick — let the user resolve it in the grid.
        onNeedsGrid && onNeedsGrid();
        return;
    }

    // Persist a credit score matching what the grid save would: the quote's band
    // top when it requires sub-780 credit, otherwise the filter value.
    const creditScore = deskingData?.credit_score;
    const filterCreditScore = (creditScore === '' || creditScore == null) ? null : Number(creditScore);
    const quoteMaxCreditScore = Number(pick.max_credit_score);
    const creditScoreValue = (!isNaN(quoteMaxCreditScore) && quoteMaxCreditScore < 780)
        ? quoteMaxCreditScore
        : filterCreditScore;

    const payload = {
        ...deskingState,
        term: pick.term,
        lender: pick.lender_name,
        tier: pick.tier,
        program_code: pick.program_code,
        credit_score: creditScoreValue,
        quotes: null,
    };
    // Link a pricing only when the caller actually has one. The create serializer
    // rejects `pricing_id: null` (required, non-nullable), and the Deal.pricing FK
    // is nullable — so an absent pricing is simply omitted, matching the grid save
    // path (which sends `undefined`, dropped by JSON).
    const pricingId = deskingData?.pricing_id ?? deskingData?.pricing;
    if (pricingId != null) {
        payload.pricing_id = pricingId;
        payload.pricing = pricingId;
    }
    if (getLeadId()) payload.leads = [getLeadId()];

    const createResp = await fetcher.createDeal(payload);
    const created = createResp?.data;
    // Carry the credit score we just persisted so the page reflects it even if the
    // response payload doesn't echo it (matches the grid save path).
    onSave && onSave(created ? { ...created, credit_score: creditScoreValue } : created);
}
